import React, { useEffect, useMemo, useState } from 'react';
import Card from '@amzn/meridian/card';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Input from '@amzn/meridian/input';
import Link from '@amzn/meridian/link';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import searchTokens from '@amzn/meridian-tokens/base/icon/search';
import { getImageQualitySiteRankings } from './api';
import { GetImageQualitySiteRankingsOutput, ReportWindow } from './model';
import { formatPct, largestDriver, passRatePct, qualityTier } from './imageQualityUtils';
import { useReportFetch, reportRequestKey } from './useReportFetch';
import FailureBreakdown from './FailureBreakdown';
import {
  AccentCard,
  Chip,
  Label,
  LoadFailed,
  PageTitle,
  PrintButton,
  PassRateBar,
  StatCard,
  textColorForTier,
  useIqColors,
} from './meridianUi';

/**
 * Network view (like the Site Leaderboard): headline tiles + every reporting site ranked best to
 * worst, with the caller's own (Boson-selected) site highlighted. Other sites are aggregate-only
 * (no drill-down): Boson only proxies requests for the user's own station.
 */
export interface AllSitesViewProps {
  /** The caller's station: sent for logging/metrics, and its row is highlighted. */
  ownSite: string;
  reportWindow: ReportWindow;
  windowLabel: string;
  /** Date range controls, shown under the headline tiles. */
  windowBar: React.ReactNode;
  onOpenOwnSite: () => void;
  /** Told the earliest operating day with data, so the page calendars can start there. */
  onDataAvailableFrom?: (day?: string) => void;
}

const AllSitesView: React.FC<AllSitesViewProps> = ({
  ownSite,
  reportWindow,
  onDataAvailableFrom,
  ...rankingsProps
}) => {
  const {
    data: rankings,
    loading,
    failed,
    retry,
  } = useReportFetch(
    () => getImageQualitySiteRankings({ warehouseId: ownSite, ...reportWindow }),
    reportRequestKey(reportWindow, ownSite)
  );

  useEffect(() => {
    onDataAvailableFrom?.(rankings?.dataAvailableFrom);
  }, [rankings, onDataAvailableFrom]);

  if (loading || !rankings) {
    return (
      <Column spacing="500">
        {rankingsProps.windowBar}
        {loading && (
          <Row alignmentHorizontal="center" spacingInset="500">
            <Loader size="large" />
          </Row>
        )}
        {failed && (
          <LoadFailed
            message="Couldn't load the site rankings."
            onRetry={retry}
            testId="iq-rankings-failed"
          />
        )}
      </Column>
    );
  }
  return <SiteRankings rankings={rankings} ownSite={ownSite} {...rankingsProps} />;
};

interface SiteRankingsProps {
  rankings: GetImageQualitySiteRankingsOutput;
  ownSite: string;
  windowLabel: string;
  windowBar: React.ReactNode;
  onOpenOwnSite: () => void;
}

const SiteRankings: React.FC<SiteRankingsProps> = ({
  rankings,
  ownSite,
  windowLabel,
  windowBar,
  onOpenOwnSite,
}) => {
  const colors = useIqColors();
  const [filter, setFilter] = useState('');
  const { thresholds } = rankings;
  const networkPct = passRatePct(rankings.passingPhotos, rankings.totalPhotos);
  const networkTier = qualityTier(networkPct, thresholds);
  const driver = rankings.failureBreakdown ? largestDriver(rankings.failureBreakdown) : null;

  // Rank is the position in the full (unfiltered) list, so filtering never renumbers sites.
  const rows = useMemo(() => {
    const needle = filter.trim().toUpperCase();
    return rankings.sites
      .map((site, i) => ({ site, rank: i + 1 }))
      .filter(({ site }) => !needle || site.warehouseId.toUpperCase().includes(needle));
  }, [rankings.sites, filter]);

  return (
    <Column spacing="500" data-testid="iq-all-sites">
      <Row alignmentHorizontal="justify" alignmentVertical="top">
        <PageTitle
          title="Evidence Capture Quality"
          sub="Associate photo quality across all return centers, ranked best to worst"
          capturedAt={rankings.latestCapturedAt}
        />
        <PrintButton />
      </Row>

      <Row spacing="400" widths="fill" alignmentVertical="stretch">
        <StatCard
          label="Image quality"
          hero={formatPct(networkPct)}
          heroColor={textColorForTier(networkTier)}
          sub="photos passing all three checks"
          testId="iq-network-card-quality"
        />
        <StatCard label="Photos" hero={rankings.totalPhotos.toLocaleString()} sub={windowLabel} />
        <StatCard label="Sites" hero={`${rankings.sites.length}`} sub="return centers reporting" />
        <StatCard
          label="Largest driver"
          hero={rankings.largestDriver?.failureLabel ?? '—'}
          sub="most common failure"
        />
      </Row>

      {windowBar}

      {rankings.failureBreakdown && (
        <AccentCard
          testId="iq-network-summary"
          aside={
            <Column spacing="400">
              <Label>Failure breakdown</Label>
              <FailureBreakdown breakdown={rankings.failureBreakdown} />
            </Column>
          }
        >
          <Text type="b500">
            {driver ? (
              <>
                <strong>{driver.failureLabel}</strong> is the largest driver across the network —{' '}
                <strong>{driver.failedPhotos.toLocaleString()}</strong> photos,{' '}
                {formatPct(driver.sharePct)} of all failures.
              </>
            ) : (
              'No failed checks in this range.'
            )}
          </Text>
        </AccentCard>
      )}

      <Row alignmentHorizontal="justify" alignmentVertical="center">
        <Column spacing="100">
          <Heading level={3} type="h400">
            Site rankings
          </Heading>
          <Text type="b200" color="secondary">
            Other sites show network-level totals only.
          </Text>
        </Column>
        <Input
          value={filter}
          onChange={setFilter}
          placeholder="Filter sites..."
          prefixIconTokens={searchTokens}
          width={260}
          size="small"
          aria-label="Filter sites"
          data-testid="iq-site-filter"
        />
      </Row>

      <Card spacingInset="none">
        <Table headerRows={1} showDividers spacing="medium" data-testid="iq-site-rankings">
          <TableRow>
            <TableCell header width={70}>
              RANK
            </TableCell>
            <TableCell header width={170}>
              SITE
            </TableCell>
            <TableCell header>IMAGE QUALITY</TableCell>
            <TableCell header width={260}>
              ASSOCIATES
            </TableCell>
            <TableCell header width={180}>
              LARGEST DRIVER
            </TableCell>
          </TableRow>
          {rows.map(({ site, rank }) => {
            const pct = passRatePct(site.passingPhotos, site.totalPhotos);
            const tier = qualityTier(pct, thresholds);
            const avgPhotos =
              site.associateCount > 0 ? Math.round(site.totalPhotos / site.associateCount) : 0;
            const isOwn = site.warehouseId === ownSite;
            return (
              <TableRow
                key={site.warehouseId}
                highlightOnHover
                backgroundColor={isOwn ? 'secondary' : undefined}
                data-testid={`iq-site-row-${site.warehouseId}`}
                data-own={isOwn}
              >
                <TableCell>
                  <Text color="secondary">{rank}</Text>
                </TableCell>
                <TableCell>
                  {isOwn ? (
                    <Row spacing="200">
                      <Link onClick={onOpenOwnSite}>
                        <Text tag="span" type="h300">
                          {site.warehouseId}
                        </Text>
                      </Link>
                      <Text type="b200" color="secondary">
                        your site
                      </Text>
                    </Row>
                  ) : (
                    <Text type="h300">{site.warehouseId}</Text>
                  )}
                </TableCell>
                <TableCell>
                  <Row spacing="500" widths={['fill', 70]}>
                    <PassRateBar
                      pct={pct}
                      thresholds={thresholds}
                      width="100%"
                      label={`${site.warehouseId} image quality`}
                    />
                    <Text type="h300" color={textColorForTier(tier)}>
                      {formatPct(pct)}
                    </Text>
                  </Row>
                </TableCell>
                <TableCell>
                  <Text>
                    {site.associateCount.toLocaleString()}
                    <Text tag="span" color="secondary">{` · ${avgPhotos} photos avg`}</Text>
                  </Text>
                </TableCell>
                <TableCell>
                  {site.largestDriver && (
                    <Chip dotColor={colors.failure(site.largestDriver.checkId)}>
                      {site.largestDriver.failureLabel}
                    </Chip>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </Table>
        {rows.length === 0 && <Text color="secondary">No sites match the filter.</Text>}
      </Card>
    </Column>
  );
};

export default AllSitesView;
